/**
 * <StudyDuel> — duel mode, supports two flavours from the same route:
 *   /study/duel?deck=<id>            → start a fresh vs-AI duel
 *   /study/duel?duel=<duel_id>       → join / resume an existing peer duel
 *
 * The route param decides everything else:
 *   • AI  — cards from `duel.cards`, answers array is `duel.answers`,
 *           score fields are `score_host` / `score_opponent`.
 *   • Peer — cards from `duel.cards`, my answers under `my_answers`,
 *            my/opp counters under `my_score` / `opp_score`, live
 *            partner progress polled every 3s.
 *
 * The scoreboard is the same shape in both modes so the UI is nearly
 * identical; peer mode adds a small "Waiting for <name>" callout while
 * the partner catches up.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { STUDY_THEME, paperBackground, CARD_TYPE_META } from '../components/study/studyTheme';
import {
  startDuel, revealCard, submitDuelAnswer, getDuel,
} from '../lib/studyApi';

const scoreBox = (mine) => ({
  flex: 1,
  padding: 20,
  borderRadius: 14,
  background: 'white',
  boxShadow: STUDY_THEME.shadow,
  border: `2px solid ${mine ? STUDY_THEME.goldDark : STUDY_THEME.line}`,
  textAlign: 'center',
});

const scoreNumber = {
  fontFamily: '"Playfair Display", Georgia, serif',
  fontSize: 64, fontWeight: 700, lineHeight: 1,
  color: STUDY_THEME.ink,
};

const duelBtn = (fg, bg) => ({
  flex: 1, padding: '14px 20px', fontSize: 15, fontWeight: 700,
  color: fg, background: bg, border: `1px solid ${fg}`,
  borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
});

export default function StudyDuel() {
  const [params] = useSearchParams();
  const deckId = params.get('deck');      // AI-mode setup
  const duelIdParam = params.get('duel'); // Peer-mode resume
  const navigate = useNavigate();

  const [duel, setDuel] = useState(null);
  const [currentCard, setCurrentCard] = useState(null);
  const [back, setBack] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [err, setErr] = useState('');
  const [lastRound, setLastRound] = useState(null);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  const isPeer = Boolean(duel?.opponent === 'peer' || duelIdParam);

  // ── Card loader (shared) ───────────────────────────────────────
  const loadCard = useCallback((duelDoc, idx) => {
    if (!duelDoc || idx >= (duelDoc.cards?.length || 0)) {
      setCurrentCard(null);
      return;
    }
    setCurrentCard(duelDoc.cards[idx]);
    setBack('');
    setRevealed(false);
    setElapsedMs(0);
    setLastRound(null);
    const t0 = Date.now();
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsedMs(Date.now() - t0), 100);
  }, []);

  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  // ── Init: fresh AI duel OR resume peer duel ───────────────────
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        if (duelIdParam) {
          const d = await getDuel(duelIdParam);
          if (cancelled) return;
          setDuel(d);
          if (d.status === 'active') {
            loadCard(d, (d.my_answers || []).length);
          }
        } else if (deckId) {
          const d = await startDuel(deckId, 10);
          if (cancelled) return;
          setDuel(d);
          loadCard(d, 0);
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message || 'Could not start duel');
      }
    }
    init();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deckId, duelIdParam]);

  // ── Peer polling: keep partner progress fresh every 3s ───────
  useEffect(() => {
    if (!duel || duel.opponent !== 'peer') return undefined;
    if (duel.status === 'complete' || duel.status === 'declined' || duel.status === 'cancelled') {
      return undefined;
    }
    pollRef.current = setInterval(async () => {
      try {
        const fresh = await getDuel(duel.id);
        setDuel((prev) => {
          if (!prev) return fresh;
          // Only overwrite fields we don't drive locally (opp side + status).
          return {
            ...prev,
            opp_progress: fresh.opp_progress,
            opp_score: fresh.opp_score,
            my_score: fresh.my_score,
            status: fresh.status,
          };
        });
      } catch { /* ignore transient */ }
    }, 3000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [duel?.id, duel?.opponent, duel?.status]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { stopTimer(); pollRef.current && clearInterval(pollRef.current); }, []);

  // ── Answer handlers ───────────────────────────────────────────
  const reveal = async () => {
    if (!currentCard) return;
    stopTimer();
    setRevealed(true);
    try {
      const d = await revealCard(currentCard.card_id);
      setBack(d.back);
    } catch { setBack('(failed to load answer)'); }
  };

  const answer = useCallback(async (correct) => {
    if (!duel || !currentCard || busy) return;
    setBusy(true);
    stopTimer();
    try {
      const resp = await submitDuelAnswer(duel.id, currentCard.card_id, correct, elapsedMs);
      setDuel(resp.duel);
      if (!isPeer) {
        setLastRound(resp.round);
        if (resp.finished) {
          setCurrentCard(null);
        } else {
          setTimeout(() => {
            const nextIdx = (resp.duel.answers || []).length;
            loadCard(resp.duel, nextIdx);
          }, 1400);
        }
      } else {
        // Peer mode: advance immediately to the next unanswered card by me.
        const nextIdx = resp.next_index;
        if (nextIdx >= (resp.duel.cards?.length || 0)) {
          setCurrentCard(null);
        } else {
          setTimeout(() => loadCard(resp.duel, nextIdx), 900);
        }
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Answer failed');
    } finally {
      setBusy(false);
    }
  }, [duel, currentCard, elapsedMs, busy, isPeer, loadCard]);

  // ── Derived state ─────────────────────────────────────────────
  const totalRounds = duel ? (duel.cards?.length || 0) : 10;
  const myAnswered = duel
    ? (isPeer ? (duel.my_answers?.length || 0) : (duel.answers?.length || 0))
    : 0;
  const oppAnswered = duel && isPeer ? (duel.opp_progress || 0) : 0;
  const myScore  = duel ? (isPeer ? (duel.my_score  ?? 0) : (duel.score_host     ?? 0)) : 0;
  const oppScore = duel ? (isPeer ? (duel.opp_score ?? 0) : (duel.score_opponent ?? 0)) : 0;
  const finished = duel && duel.status === 'complete';
  const waitingOnPartner = isPeer && duel && duel.status === 'invited';
  const iAmDoneOppNot = isPeer && duel && !finished && !currentCard
    && myAnswered >= totalRounds && oppAnswered < totalRounds;
  const meta = currentCard ? CARD_TYPE_META[currentCard.type] || CARD_TYPE_META.standard : null;

  const oppLabel = isPeer
    ? (duel?.opp_name || duel?.opp_email || 'Colleague')
    : `AI Tutor · ${Math.round((duel?.ai_accuracy || 0) * 100)}%`;

  return (
    <div style={paperBackground()} data-testid="study-duel-page">
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 20px' }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 20,
        }}>
          <button
            onClick={() => navigate(isPeer ? '/study/duels' : '/study')}
            data-testid="duel-exit-btn"
            style={{
              padding: '8px 14px', fontSize: 13, fontWeight: 600,
              background: 'transparent', color: STUDY_THEME.inkSoft,
              border: `1px solid ${STUDY_THEME.line}`, borderRadius: 6,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            ← {isPeer ? 'Duel Inbox' : 'Deck Hub'}
          </button>
          <div style={{
            fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase',
            fontWeight: 700, color: STUDY_THEME.goldDeep,
          }} data-testid="duel-round-label">
            {finished ? 'Match complete'
              : waitingOnPartner ? 'Waiting for partner'
              : `Round ${myAnswered + 1} of ${totalRounds}`}
          </div>
          {!finished && !waitingOnPartner && duel && currentCard && (
            <div style={{
              fontSize: 13, color: STUDY_THEME.inkSoft, fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }} data-testid="duel-timer">
              ⏱ {(elapsedMs / 1000).toFixed(1)}s
            </div>
          )}
        </div>

        {err && (
          <div data-testid="duel-error" style={{
            padding: 20, borderRadius: 10, background: '#FEE2E2',
            color: '#991B1B', marginBottom: 20,
          }}>{err}</div>
        )}

        {/* Scoreboard */}
        {duel && (
          <div style={{ display: 'flex', gap: 16, marginBottom: 28 }}>
            <div style={scoreBox(true)} data-testid="duel-score-you">
              <div style={{
                fontSize: 11, letterSpacing: 1.5, fontWeight: 700,
                color: STUDY_THEME.goldDeep, textTransform: 'uppercase',
              }}>You{isPeer && ` · ${myAnswered}/${totalRounds}`}</div>
              <div style={scoreNumber}>{myScore}</div>
            </div>
            <div style={{
              alignSelf: 'center', fontSize: 20, fontWeight: 700,
              color: STUDY_THEME.muted, fontFamily: '"Playfair Display", serif',
            }}>vs</div>
            <div style={scoreBox(false)} data-testid="duel-score-opp">
              <div style={{
                fontSize: 11, letterSpacing: 1.5, fontWeight: 700,
                color: STUDY_THEME.muted, textTransform: 'uppercase',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{oppLabel}{isPeer && ` · ${oppAnswered}/${totalRounds}`}</div>
              <div style={scoreNumber}>{oppScore}</div>
            </div>
          </div>
        )}

        {/* Peer: waiting-for-partner idle screen */}
        {waitingOnPartner && (
          <div data-testid="duel-waiting" style={{
            background: 'white', padding: 40, borderRadius: 16,
            textAlign: 'center', boxShadow: STUDY_THEME.shadow,
          }}>
            <div style={{ fontSize: 42, marginBottom: 12 }}>📨</div>
            <h2 style={{
              fontFamily: '"Playfair Display", serif', fontSize: 28,
              margin: '0 0 8px',
            }}>Waiting for {duel.opp_name || duel.opp_email}</h2>
            <p style={{ color: STUDY_THEME.inkSoft, margin: 0, fontSize: 14 }}>
              They&apos;ll see the invitation in their Buddy Duels inbox.
              You can close this tab — we&apos;ll pick up where you left off
              when they accept.
            </p>
          </div>
        )}

        {/* Peer: I finished, partner is still playing */}
        {iAmDoneOppNot && (
          <div data-testid="duel-i-done" style={{
            background: 'white', padding: 32, borderRadius: 16,
            textAlign: 'center', boxShadow: STUDY_THEME.shadow, marginBottom: 20,
          }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>⏳</div>
            <h2 style={{
              fontFamily: '"Playfair Display", serif', fontSize: 22,
              margin: '0 0 6px',
            }}>You&apos;re done · Waiting on {duel.opp_name || 'partner'}</h2>
            <p style={{ color: STUDY_THEME.inkSoft, margin: 0, fontSize: 13 }}>
              Final score will lock in once they finish all {totalRounds} rounds.
              Refresh or come back later — this page updates automatically.
            </p>
          </div>
        )}

        {/* Current card */}
        {!finished && !waitingOnPartner && currentCard && meta && (
          <div
            data-testid="duel-card"
            style={{
              background: 'white', padding: 40, borderRadius: 16,
              boxShadow: STUDY_THEME.shadow,
              borderTop: `6px solid ${meta.accent}`,
            }}
          >
            <div style={{
              fontSize: 12, letterSpacing: 1, textTransform: 'uppercase',
              fontWeight: 700, color: meta.accent, marginBottom: 14,
            }}>
              {meta.emoji} {meta.label}
            </div>

            {!revealed ? (
              <>
                <div style={{
                  fontFamily: '"Playfair Display", serif', fontSize: 22,
                  color: STUDY_THEME.ink, marginBottom: 24, lineHeight: 1.4,
                }} data-testid="duel-card-front">
                  {currentCard.front || `A ${meta.label.toLowerCase()} card. Think of your answer, then reveal.`}
                </div>
                {(currentCard.related_modules || []).length > 0 && (
                  <div style={{ marginBottom: 20, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {currentCard.related_modules.map((m) => (
                      <span key={m} style={{
                        fontSize: 11, color: STUDY_THEME.goldDeep,
                        background: 'rgba(252,211,89,0.20)',
                        padding: '3px 9px', borderRadius: 4, fontWeight: 600,
                      }}>{m}</span>
                    ))}
                  </div>
                )}
                <button
                  onClick={reveal}
                  data-testid="duel-reveal-btn"
                  style={{
                    padding: '14px 28px', fontSize: 15, fontWeight: 700,
                    background: STUDY_THEME.ink, color: 'white',
                    border: 'none', borderRadius: 8, cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  Reveal Answer
                </button>
              </>
            ) : (
              <>
                <div style={{
                  fontSize: 15, lineHeight: 1.7, color: STUDY_THEME.inkSoft,
                  whiteSpace: 'pre-wrap', marginBottom: 24,
                }}>{back}</div>
                <div style={{
                  fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase',
                  fontWeight: 700, color: STUDY_THEME.goldDeep, marginBottom: 10,
                }}>Did you get it?</div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    onClick={() => answer(true)}
                    disabled={busy}
                    data-testid="duel-answer-correct"
                    style={duelBtn('#16A34A', '#BBF7D0')}
                  >
                    ✓ Correct
                  </button>
                  <button
                    onClick={() => answer(false)}
                    disabled={busy}
                    data-testid="duel-answer-wrong"
                    style={duelBtn('#DC2626', '#FEE2E2')}
                  >
                    ✗ Wrong
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* AI mode: last round toast */}
        {!isPeer && lastRound && !finished && (
          <div
            data-testid="duel-round-result"
            style={{
              marginTop: 20, padding: 14, borderRadius: 10,
              background: lastRound.host_pt ? '#DCFCE7'
                        : lastRound.opp_pt ? '#FEE2E2' : STUDY_THEME.paperWarm,
              textAlign: 'center', fontWeight: 600,
              color: lastRound.host_pt ? '#166534'
                   : lastRound.opp_pt ? '#991B1B' : STUDY_THEME.inkSoft,
            }}
          >
            {lastRound.host_pt ? '🎉 You won this round!' :
             lastRound.opp_pt ? '💥 AI wins this round.' :
             'Neutral round — no point.'}
            <span style={{
              marginLeft: 12, fontWeight: 500, fontSize: 12,
              color: STUDY_THEME.muted,
            }}>
              You: {lastRound.student_correct ? '✓' : '✗'} in {(lastRound.student_ms / 1000).toFixed(1)}s ·
              {' '}AI: {lastRound.ai_correct ? '✓' : '✗'} in {(lastRound.ai_ms / 1000).toFixed(1)}s
            </span>
          </div>
        )}

        {/* Match complete */}
        {finished && duel && (
          <div
            data-testid="duel-finished"
            style={{
              background: 'white', padding: 40, borderRadius: 16,
              textAlign: 'center', boxShadow: STUDY_THEME.shadow,
            }}
          >
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {myScore > oppScore ? '🏆' : myScore < oppScore ? (isPeer ? '💥' : '🤖') : '🤝'}
            </div>
            <h2 style={{
              fontFamily: '"Playfair Display", serif', fontSize: 32, margin: '0 0 12px',
            }}>
              {myScore > oppScore ? 'You won!' :
               myScore < oppScore ? `${isPeer ? (duel.opp_name || 'Partner') : 'AI'} wins this one.` :
               'It\'s a tie!'}
            </h2>
            <p style={{ color: STUDY_THEME.inkSoft, marginBottom: 24 }}>
              Final score: <b>{myScore}</b> — <b>{oppScore}</b>
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              {duel.deck_id && myScore <= oppScore && (
                <button
                  onClick={() => navigate(`/study/session?deck=${duel.deck_id}&focus=weakest`)}
                  data-testid="duel-drill-weakest-btn"
                  style={{
                    padding: '12px 22px', fontSize: 14, fontWeight: 700,
                    background: '#DC2626', color: 'white',
                    border: '1px solid #B91C1C', borderRadius: 8,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  🎯 Drill your 4 weakest cards
                </button>
              )}
              <button
                onClick={() => navigate(isPeer ? '/study/duels' : '/study')}
                data-testid="duel-hub-btn"
                style={{
                  padding: '12px 22px', fontSize: 14, fontWeight: 700,
                  background: STUDY_THEME.gold, color: STUDY_THEME.ink,
                  border: `1px solid ${STUDY_THEME.goldDark}`, borderRadius: 8,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {isPeer ? 'Back to Duel Inbox' : 'Back to Deck Hub'}
              </button>
            </div>
          </div>
        )}

        {!duel && !err && (
          <div style={{ padding: 40, textAlign: 'center', color: STUDY_THEME.muted }}>
            Setting up the duel…
          </div>
        )}
      </div>
    </div>
  );
}
